import { PageSchema } from '@vs-shop/schemas/url/queryParams/pagination';
import { Effect, Option } from 'effect';

import { RuntimeClientAtom } from '../../runtime/RuntimeClient';
import * as Atom from 'effect/unstable/reactivity/Atom';
import { tagged } from '../../data/util/tagged';

// Define a Data.tagged type for type-safe atom keys with Equal implementation
type ContentBlockProductsPageKey = {
  readonly _tag: 'ContentBlockProductsPageKey';
  readonly blockIndex: number;
};

const ContentBlockProductsPageKey = tagged<ContentBlockProductsPageKey>(
  'ContentBlockProductsPageKey'
);

// b1Page, b2Page...
export const getPageParamName = (blockIndex: number): string => `b${String(blockIndex)}Page`;

export const contentBlockProductsPageSearchParamAtomFamily = Atom.family(
  (key: ContentBlockProductsPageKey) => {
    const paramName = getPageParamName(key.blockIndex);
    return Atom.searchParam(paramName, {
      schema: PageSchema,
    }).pipe(Atom.withServerValue(() => Option.none()));
  }
);

export const currentContentBlockProductsPageAtomFamily = Atom.family(
  (key: ContentBlockProductsPageKey) => {
    const pageSearchParamAtom = contentBlockProductsPageSearchParamAtomFamily(key);
    return Atom.map(pageSearchParamAtom, (pageOption) =>
      Option.getOrElse(pageOption, () => 1)
    ).pipe(Atom.withServerValue(() => 1));
  }
);

export type ChangeContentBlockProductsPageAtomPromise = (
  value: number | typeof Atom.Reset | typeof Atom.Interrupt
) => Promise<void>;

export const changeContentBlockProductsPageAtomFnFamily = Atom.family(
  (key: ContentBlockProductsPageKey) =>
    RuntimeClientAtom.fn((page: number, ctx) =>
      Effect.gen(function* () {
        yield* Effect.logDebug('🔄 changeContentBlockProductsPageAtomFn', {
          blockIndex: key.blockIndex,
          page,
        });
        const pageSearchParamAtom = contentBlockProductsPageSearchParamAtomFamily(key);
        // Set to None (removes URL param) if page 1, otherwise Some(page)
        ctx.set(pageSearchParamAtom, page === 1 ? Option.none() : Option.some(page));
      })
    )
);

export const contentBlockProductsLastPageLoadedAtomFamily = Atom.family(
  (key: ContentBlockProductsPageKey) => {
    const currentPageAtom = currentContentBlockProductsPageAtomFamily(key);
    return Atom.readable((ctx) => {
      return ctx.get(currentPageAtom);
    }).pipe(Atom.withServerValue(() => 1));
  }
);

export const createContentBlockProductsPageKey = (blockIndex: number) =>
  ContentBlockProductsPageKey({ blockIndex });
